import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import { useAuthContext } from 'AuthContext';
import { useMeetingHubContext } from '../MeetingHubContext';
import MeetingService from 'services/MeetingService';
import { useHubEvent } from 'hooks/useHubEvent';
import AttendanceTableActions from './AttendanceTableActions';
import AttendanceTable from './AttendanceTable';
import VotingRoleRequested from './VotingRoleRequestedModal';
import CallReminderModal from './CallReminderModal';
import MeetingHubEvents from 'store/enums/MeetingHubActions';
import { Attendee, CallReminderNotification, VotingRoleRequest } from 'store/types/Meeting';

const AttendanceSection: React.FunctionComponent = () => {
  const { profile, isStaff } = useAuthContext();
  const { connection, isConnected, meetingId, attendeeId, attendees, refetchAttendees } = useMeetingHubContext();
  const [votingRoleRequestedModalOpen, setVotingRoleRequestedModalOpen] = useState<boolean>(false);
  const [callReminderModalOpen, setCallReminderModalOpen] = useState<boolean>(false);
  const [requester, setRequester] = useState<Attendee | undefined>();
  const [callReminderMessage, setCallReminderMessage] = useState<string>('');
  const [attendanceLock, setAttendanceLock] = useState<boolean>(false);

  useEffect(() => {
    if (!attendanceLock && isConnected && meetingId && profile && attendeeId && attendees && refetchAttendees) {
      setAttendanceLock(true);

      MeetingService.addAttendee({
        id: attendeeId,
        meetingId: meetingId,
        name: `${profile.customer?.firstname} ${profile.customer?.lastname}`,
        email: profile.customer?.email,
        company: profile.customer?.companyName,
        affiliate: profile.affiliate?.affiliateName,
        isManuallyAdded: false,
        isVotingMember: false,
      })
        .then(() => {
          refetchAttendees();
        })
        .catch(() => setAttendanceLock(false));
    }
  }, [attendanceLock, attendeeId, attendees, isConnected, meetingId, profile, refetchAttendees]);

  useEffect(() => {
    if (!isConnected) {
      setAttendanceLock(false);
    }
  }, [isConnected]);

  const handlePresidingChairModalClose = useCallback(() => {
    setVotingRoleRequestedModalOpen(false);
    setRequester(undefined);
  }, []);

  const handleCallReminderModalClose = useCallback(() => {
    setCallReminderModalOpen(false);
    setCallReminderMessage('');
  }, []);

  const handleVotingRoleRequested = useCallback(
    (payload: VotingRoleRequest) => {
      if (payload.meetingId !== meetingId) return;

      const attendee = attendees?.find((a) => a.id === payload.attendeeId);
      if (attendee) {
        setRequester(attendee);
        setVotingRoleRequestedModalOpen(true);
      }
    },
    [attendees, meetingId]
  );

  const handleCallReminderReceived = useCallback(
    (payload: CallReminderNotification) => {
      if (payload.meetingId !== meetingId) return;

      setCallReminderMessage(payload.message);
      setCallReminderModalOpen(true);
    },
    [meetingId]
  );

  useHubEvent<VotingRoleRequest>(connection, MeetingHubEvents.VotingRoleRequested, handleVotingRoleRequested);
  useHubEvent<CallReminderNotification>(connection, MeetingHubEvents.CallReminderReceived, handleCallReminderReceived);

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Attendance ({attendees?.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display={'flex'}>
            <StarIcon fontSize="small" className="mr-1" /> - Presiding Chair
          </Box>
          <AttendanceTable />
          {isStaff && <AttendanceTableActions />}
        </AccordionDetails>
      </Accordion>
      <VotingRoleRequested
        data={requester}
        open={votingRoleRequestedModalOpen}
        onClose={handlePresidingChairModalClose}
        onSubmit={handlePresidingChairModalClose}
      />
      <CallReminderModal
        open={callReminderModalOpen}
        message={callReminderMessage}
        onClose={handleCallReminderModalClose}
        onSubmit={handleCallReminderModalClose}
      />
    </>
  );
};

export default AttendanceSection;
